import { useNavigate, useNavigationType } from "react-router";

import { useURL } from "~/contexts";

import { Button } from "../ui/button";

/**
 * SFCC-11462 — PLP-appropriate error UI. Rendered inline by the PLP route when
 * a listing load fails (the loader returns the error-as-data sentinel), and by
 * the `/search` + legacy `/shop/:categoryId` routes' `ErrorBoundary`.
 *
 * Distinct from `PDPErrorBoundary`'s "trouble loading this product" copy — a
 * PLP failure is a listing failure, not a product failure.
 */
export function PlpErrorBoundaryView() {
  const url = useURL();
  const navigationType = useNavigationType();
  const navigate = useNavigate();

  // Offer "Go Back" only when we reached this error via an in-app navigation
  // (PUSH/REPLACE — e.g. clicking View All), so back-nav lands on a same-site
  // page and never leaves the site or hits about:blank. A fresh document load
  // (or back/forward) is "POP" → "Go Home". `useNavigationType` is stable
  // across SSR + hydration (the initial action is "POP" on both), so there's no
  // flicker — unlike `location.key` (regenerated client-side) or
  // `document.referrer` (not updated on SPA navs).
  const canGoBack = navigationType !== "POP";

  return (
    <section className="flex min-h-[50vh] flex-col items-center justify-center px-4 py-16 text-center">
      <h1 className="mb-4 text-2xl font-semibold">
        Something went wrong loading products
      </h1>
      <p className="mb-6 text-sm text-gray-600">
        We&apos;re having trouble loading this page. Please try again.
      </p>
      <div className="flex gap-4">
        {canGoBack ? (
          <Button
            variant="cta"
            size="5"
            type="button"
            onClick={() => navigate(-1)}
          >
            Go Back
          </Button>
        ) : (
          <Button variant="cta" size="5" as="a" href={url("/")}>
            Go Home
          </Button>
        )}
      </div>
    </section>
  );
}
